import React from 'react'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '~/lib/ui/table'
import { TagIcon } from '@heroicons/react/24/outline'
import { useCandidate } from './CandidateProvider'
import { StatusIndicator } from './ResumeReviewDetail'
import moment from 'moment'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '~/util/api'
import { useFormatTime } from '~/hooks/useFormatTime'
import { Loading } from '~/lib/ui/loading'

export const useServiceRequests = () => {
  const { candidateId } = useCandidate()

  const queryPath = `/coach/dashboard_candidates/${candidateId}/service_requests.json`

  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { data, error, isFetching, isLoading, isFetched } = useQuery({
    queryKey: ['serviceRequests', candidateId],
    queryFn,
    enabled: !!candidateId,
    refetchOnWindowFocus: false,
  })

  if (error) {
    console.error('Error retrieving service requests:', error)
    return {
      serviceRequests: [],
      isFetching: false,
      isLoading: false,
      isFetched: true,
      hasError: true,
    }
  }

  const serviceRequests = data?.serviceRequests || []
  return { serviceRequests, isFetching, isLoading, isFetched, hasError: false }
}

const ServicesSection = () => {
  const { serviceRequests, isLoading, isFetching, hasError } =
    useServiceRequests()
  const { candidate: { programLevel } = {} } = useCandidate()
  const showLoadingRow =
    (isLoading || isFetching) && serviceRequests.length === 0

  return (
    <Table className="w-full" tableClass="rounded-2xl border border-silver">
      <ServicesHeading />
      <TableBody>
        {showLoadingRow && (
          <TableRow>
            <TableCell colSpan={6} className="py-8 text-center">
              <div className="flex items-center justify-center gap-3 text-gray-600">
                <Loading className="h-6 w-6" />
                <span>Loading services...</span>
              </div>
            </TableCell>
          </TableRow>
        )}
        {hasError && !showLoadingRow && (
          <TableRow>
            <TableCell colSpan={6} className="py-8 text-center text-gray-500">
              Unable to load services right now.
            </TableCell>
          </TableRow>
        )}
        {serviceRequests?.map((serviceRequest, idx) => (
          <ServicesRow
            key={`serviceRequest-${idx}`}
            serviceRequest={serviceRequest}
          />
        ))}
        {!showLoadingRow &&
          !hasError &&
          (programLevel === 'BRONZE' ||
            programLevel === 'SOLO_PLUS' ||
            programLevel == 'EASE') && (
            <TableRow className="text-center">
              <TableCell colSpan={6} className="text-gray-500">
                There are no one-to-one coaching services for this candidate due
                to their program level. Chat history will appear in Coach Notes.
              </TableCell>
            </TableRow>
          )}
      </TableBody>
    </Table>
  )
}
export default ServicesSection

const ServicesRow = ({ serviceRequest, ...props }) => {
  const { displayName, createdAt, dueAt, requestType, requestCategory } =
    serviceRequest || {}
  const formatTime = useFormatTime()
  const requestTypeName = requestType.split('::')[1]
  const displayDueAt = dueAt ? formatTime(moment(dueAt), 'short') : 'N/A'
  const displayCreatedAt = createdAt
    ? formatTime(moment(createdAt), 'short')
    : 'N/A'

  let linkPath = '#'
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    params.set('tab', 'coach_services')
    params.set('detail_type', requestTypeName)
    params.set('detail_item_id', serviceRequest.id)
    linkPath = `${window.location.pathname}?${params.toString()}`
  }

  return (
    <TableRow
      {...props}
      className="odd:bg-white even:bg-[#e2e2e2] even:bg-opacity-[29%]"
    >
      <TableCell>{displayName}</TableCell>
      <TableCell>
        <StatusIndicator serviceRequest={serviceRequest} />
      </TableCell>
      <TableCell>{displayCreatedAt}</TableCell>
      <TableCell>{displayDueAt}</TableCell>
      <TableCell>
        <Tags requestCategory={requestCategory} />
      </TableCell>
      <TableCell>
        <a href={linkPath} className="cursor-pointer !underline">
          View
        </a>
      </TableCell>
    </TableRow>
  )
}

const Tags = ({ requestCategory }) => {
  if (requestCategory === 'multi') {
    return (
      <div className="font-sm text-medium max-w-max rounded-lg bg-[#D78023] px-4 py-2 text-white">
        <TagIcon className="mr-2 inline h-4" />
        Multi-Step Service
      </div>
    )
  } else if (requestCategory === 'reoccurring') {
    return (
      <div className="font-sm text-medium max-w-max rounded-lg bg-[#2383D7] px-4 py-2 text-white">
        <TagIcon className="mr-2 inline h-4" />
        Reocurring Service
      </div>
    )
  } else {
    return (
      <div className="font-sm text-medium max-w-max rounded-lg bg-[#5565DE] px-4 py-2 text-white">
        <TagIcon className="mr-2 inline h-4" />
        One Time Service
      </div>
    )
  }
}

const ServicesHeading = () => {
  const cells = [
    'Service Type',
    'Status',
    'Date Requested',
    'Due Date',
    'Type',
    'Action',
  ]
  return (
    <TableHeader className="h-6 bg-intoo-blue-medium">
      <TableRow className="text-lg text-white hover:bg-intoo-blue-medium">
        {cells.map((cell, index) => (
          <TableHead key={index} className="font-medium text-white">
            {cell}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  )
}
